import type { Context } from "./cond";
import type { Effect } from "./effect";
import { BattleStatus } from "./pet";
import type { Pet } from "./pet";
import type { Situation } from "./round";
import { Timing } from "./timing";

/** 条件、描述都只读取基础局面；同步不执行主体、不派发事件、不消耗随机数。 */
export function reconcileSoulmarkEffects(context: Context) {
    /**
     * side，供本模块相关规则使用。
     */
    const side = context.side ?? "left";
    /**
     * left，供本模块相关规则使用。
     */
    const left = side === "left" ? context.sbjSeer : context.objSeer;
    /**
     * right，供本模块相关规则使用。
     */
    const right = side === "left" ? context.objSeer : context.sbjSeer;
    /**
     * pets，供本模块相关规则使用。
     */
    const pets = [...left.petPackage, ...right.petPackage];
    /**
     * managedSources，供本模块相关规则使用。
     */
    const managedSources = new Set<string>();
    /**
     * desired，供本模块相关规则使用。
     */
    const desired = new Map<
        string,
        { /**
         * target，保存当前实例的target状态。
         */
        target: Pet; /**
         * definition，保存当前实例的definition状态。
         */
        definition: Effect; /**
         * context，保存当前实例的context状态。
         */
        context: Context }
    >();
    /**
     * deathSubject，供本模块相关规则使用。
     */
    const deathSubject =
        context.timing === Timing.petDead ? (context.eventPet ?? context.sbjPet) : undefined;
    for (const [seer, opponent] of [
        [left, right],
        [right, left],
    ] as const) {
        for (const owner of seer.petPackage) {
            for (const candidate of owner.getSoulmarkEffectCandidates()) {
                /**
                 * definition，供本模块相关规则使用。
                 */
                const definition = candidate.definition;
                /**
                 * source，供本模块相关规则使用。
                 */
                const source = definition.soulmarkSource!;
                managedSources.add(source);
                /**
                 * targetSeer，供本模块相关规则使用。
                 */
                const targetSeer = candidate.bindEnemy ? opponent : seer;
                /**
                 * otherSeer，供本模块相关规则使用。
                 */
                const otherSeer = candidate.bindEnemy ? seer : opponent;
                /**
                 * target，供本模块相关规则使用。
                 */
                const target = candidate.bindEnemy
                    ? targetSeer.getBattlePet()
                    : targetSeer.petPackage.find((pet) => pet.序号 === candidate.targetId);
                /**
                 * other，供本模块相关规则使用。
                 */
                const other = otherSeer.getBattlePet();
                if (!owner.soulmarkEnabled || !target || !other) continue;
                // 自身正式阵亡事件先执行完，再在步骤提交时撤销死亡来源。
                if (
                    (owner.战斗状态 === BattleStatus.DEAD && owner !== deathSubject) ||
                    (target.战斗状态 === BattleStatus.DEAD && target !== deathSubject)
                )
                    continue;
                /**
                 * state，供本模块相关规则使用。
                 */
                const state: Context = {
                    sbjPet: target,
                    objPet: other,
                    sbjSeer: targetSeer,
                    objSeer: otherSeer,
                    side: targetSeer === left ? "left" : "right",
                };
                if (definition.presenceCond && !definition.presenceCond(state)) continue;
                desired.set(source, { target, definition, context: state });
            }
        }
    }
    for (const pet of pets) {
        /**
         * retained，供本模块相关规则使用。
         */
        const retained = (effect: Effect) =>
            !effect.soulmarkSource ||
            !managedSources.has(effect.soulmarkSource) ||
            desired.get(effect.soulmarkSource)?.target === pet;
        if (pet.常驻类效果.all().some((effect) => !retained(effect)))
            pet.常驻类效果.retain(retained);
    }
    for (const [source, entry] of desired) {
        /**
         * instance，供本模块相关规则使用。
         */
        let instance = entry.target.常驻类效果
            .all()
            .find((effect) => effect.soulmarkSource === source);
        if (!instance) {
            entry.target.registerPersistentEffect(entry.definition);
            instance = entry.target.常驻类效果
                .all()
                .find((effect) => effect.soulmarkSource === source)!;
        }
        instance.displayDescription = instance.effect.describe(entry.context);
    }
}

/** 提交领域快照前刷新；回看已有快照不调用此入口。 */
export function reconcileSituation(state: Pick<Situation, "leftSeer" | "rightSeer">) {
    /**
     * pet，供本模块相关规则使用。
     */
    const pet = state.leftSeer.getBattlePet();
    /**
     * target，供本模块相关规则使用。
     */
    const target = state.rightSeer.getBattlePet();
    if (pet && target)
        reconcileSoulmarkEffects({
            sbjPet: pet,
            objPet: target,
            sbjSeer: state.leftSeer,
            objSeer: state.rightSeer,
            side: "left",
        });
}
